"use client";

import { IconChevronRightSm } from "@teral/icons";
import * as React from "react";
import { cn } from "../lib/utils";
import { Button } from "./buttons/button";
import { InfoTooltip } from "./info-tooltip";

export type ChevronState = "hidden" | "interactive" | "disabled";

export type SettingCardBorder = "top" | "middle" | "bottom" | "both" | "none" | "default";

type SettingCardProps = {
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  border?: SettingCardBorder;
  contentWidth?: string;
  icon?: React.ReactNode;
  iconClassName?: string;
  expandable?: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  chevronState?: ChevronState;
  truncateDescription?: boolean;
};

const SettingCardGroupContext = React.createContext(false);

function SettingCardGroup({ children }: { children: React.ReactNode }) {
  return (
    <SettingCardGroupContext.Provider value={true}>
      <div className="border rounded-xl overflow-hidden divide-y divide-grayA-4 bg-raised shadow-xs">
        {children}
      </div>
    </SettingCardGroupContext.Provider>
  );
}
SettingCardGroup.displayName = "SettingCardGroup";

function SettingCard({
  title,
  description,
  children,
  className,
  border = "default",
  contentWidth = "w-[420px]",
  icon,
  iconClassName,
  expandable,
  defaultExpanded = false,
  expanded,
  onExpandedChange,
  chevronState,
  truncateDescription = false,
}: SettingCardProps) {
  const [internalExpanded, setInternalExpanded] = React.useState(defaultExpanded);
  const isControlled = expanded !== undefined;
  const isExpanded = isControlled ? expanded : internalExpanded;
  const contentRef = React.useRef<HTMLDivElement>(null);
  const innerRef = React.useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = React.useState(0);
  const inGroup = React.useContext(SettingCardGroupContext);

  React.useEffect(() => {
    const inner = innerRef.current;
    if (!inner) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContentHeight(entry.borderBoxSize[0].blockSize);
      }
    });
    observer.observe(inner);
    return () => observer.disconnect();
  }, []);

  // Determine effective chevron state
  const effectiveChevronState: ChevronState =
    chevronState ?? (expandable ? "interactive" : "hidden");

  const shouldShowChevron = effectiveChevronState !== "hidden";
  const isInteractive = effectiveChevronState === "interactive" && expandable;

  const getBorderRadiusClass = () => {
    if (inGroup) {
      return "";
    }
    if (border === "none" || border === "default") {
      return "";
    }
    if (border === "top") {
      return "rounded-t-xl";
    }
    if (border === "bottom") {
      return !expandable || !isExpanded ? "rounded-b-xl" : "";
    }
    if (border === "both") {
      const bottom = !expandable || !isExpanded ? "rounded-b-xl" : "";
      return cn("rounded-t-xl", bottom);
    }
    return "";
  };

  const borderClass = inGroup
    ? {}
    : {
        border: border !== "none",
        "border-t-0": border === "bottom",
        "border-b-0": border === "top",
      };

  const expandedBottomRadius =
    !inGroup && expandable && isExpanded && (border === "bottom" || border === "both")
      ? "rounded-b-xl"
      : "";

  const handleToggle = () => {
    if (!isInteractive) {
      return;
    }
    const willExpand = !isExpanded;
    if (willExpand) {
      contentRef.current?.addEventListener(
        "transitionend",
        () => {
          const inner = innerRef.current;
          if (!inner) {
            return;
          }
          const overflow = inner.getBoundingClientRect().bottom - window.innerHeight;
          if (overflow > 0) {
            findScrollParent(inner).scrollBy({ top: overflow + 16, behavior: "smooth" });
          }
        },
        { once: true },
      );
    }
    if (!isControlled) {
      setInternalExpanded(willExpand);
    }
    onExpandedChange?.(willExpand);
  };

  return (
    <div
      className={cn("w-full bg-raised", getBorderRadiusClass(), borderClass, expandedBottomRadius)}
    >
      <div
        className={cn(
          "px-4 py-[18px] lg:w-full flex gap-6 lg:justify-between lg:items-center flex-col lg:flex-row group",
          isInteractive && "cursor-pointer",
          className,
        )}
        onKeyDown={(e) => {
          if (!isInteractive) {
            return;
          }
          if (e.key === "Enter") {
            e.preventDefault();
            handleToggle();
          }
        }}
        onClick={isInteractive ? handleToggle : undefined}
      >
        <div className="flex gap-4 items-center">
          {icon && (
            <div
              className={cn(
                "size-8 rounded-lg border bg-raised text-gray-11 shadow-xs flex items-center justify-center shrink-0 dark:bg-grayA-3",
                iconClassName,
              )}
            >
              {icon}
            </div>
          )}
          <div className="flex flex-col gap-1 text-sm w-fit">
            <div className="font-medium text-gray-12 text-sm leading-4 tracking-normal">
              {title}
            </div>
            <InfoTooltip
              asChild
              content={
                <div className="whitespace-pre-wrap text-xs break-all max-w-xs">{description}</div>
              }
              position={{ side: "bottom", align: "start" }}
              disabled={!truncateDescription}
            >
              <div
                className={cn(
                  "font-normal text-gray-11 text-xs leading-4 tracking-normal max-w-[600px]",
                  truncateDescription && "truncate",
                )}
              >
                {description}
              </div>
            </InfoTooltip>
          </div>
        </div>
        <div className={cn("flex w-full items-center gap-4", contentWidth)}>
          {children}
          {shouldShowChevron && (
            <IconChevronRightSm
              className={cn(
                "text-gray-10 transition-all duration-300 ease-out shrink-0",
                isExpanded && "rotate-90",
                effectiveChevronState !== "disabled" && "group-hover:text-gray-11",
                effectiveChevronState === "disabled" && "opacity-40 cursor-not-allowed",
              )}
            />
          )}
        </div>
      </div>
      {expandable && (
        <div
          ref={contentRef}
          className="overflow-hidden transition-all duration-300 ease-out"
          style={{
            maxHeight: isExpanded ? `${contentHeight}px` : "0px",
          }}
        >
          <div
            ref={innerRef}
            className={cn(
              "border-t transition-all duration-300 ease-out",
              isExpanded ? "opacity-100 translate-y-0 delay-75" : "opacity-0 -translate-y-2",
            )}
          >
            {expandable}
          </div>
        </div>
      )}
    </div>
  );
}

// Source - https://stackoverflow.com/a/78682259
// Posted by dgropp
const scrollStyles = ["scroll", "auto"];
function findScrollParent(element: HTMLElement | null): HTMLElement | Window {
  const parent = element?.parentElement;
  if (!parent) {
    return window;
  }
  const { overflowY } = getComputedStyle(parent);
  if (scrollStyles.includes(overflowY)) {
    return parent;
  }
  return findScrollParent(parent);
}

SettingCard.displayName = "SettingCard";

type SettingsZoneVariant = "danger" | "warning";

const SettingsZoneContext = React.createContext<SettingsZoneVariant>("danger");

const zoneStyles: Record<SettingsZoneVariant, { heading: string; border: string; hint: string }> = {
  danger: {
    heading: "text-error-11",
    border: "border-errorA-6 divide-errorA-5",
    hint: "Permanent actions. They cannot be undone.",
  },
  warning: {
    heading: "text-warning-11",
    border: "border-warningA-7 divide-warningA-6",
    hint: "Changes that affect how this behaves for everyone.",
  },
};

function SettingsZone({
  children,
  className,
  variant,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  variant: SettingsZoneVariant;
  title: string;
}) {
  const styles = zoneStyles[variant];
  return (
    <SettingsZoneContext.Provider value={variant}>
      <section
        className={cn("w-full lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-x-10", className)}
      >
        <div className="mb-4 flex flex-col gap-1 px-1 lg:sticky lg:top-24 lg:mb-0 lg:self-start lg:px-0">
          <h2 className={cn("m-0 text-label", styles.heading)}>
            <span aria-hidden="true">/ </span>
            {title}
          </h2>
          <p className="mt-1 hidden text-sm leading-5 text-gray-11 lg:block">{styles.hint}</p>
        </div>
        <div
          className={cn(
            "rounded-xl border overflow-hidden divide-y bg-raised shadow-xs",
            styles.border,
          )}
        >
          {children}
        </div>
      </section>
    </SettingsZoneContext.Provider>
  );
}

SettingsZone.displayName = "SettingsZone";

/**
 * A settings section: title and a one-line hint in a sticky label column on
 * wide screens, the setting cards beside it. Stacks on narrow screens.
 */
function SettingsSection({
  title,
  description,
  children,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn("w-full lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-x-10", className)}
    >
      <div className="mb-4 flex flex-col gap-1 px-1 lg:sticky lg:top-24 lg:mb-0 lg:self-start lg:px-0">
        <h2 className="m-0 text-label text-gray-10">
          <span aria-hidden="true" className="text-signal">
            /{" "}
          </span>
          {title}
        </h2>
        {description ? (
          <p className="mt-1 hidden text-sm leading-5 text-gray-11 lg:block">{description}</p>
        ) : null}
      </div>
      <div className="flex min-w-0 flex-col gap-4">{children}</div>
    </section>
  );
}

SettingsSection.displayName = "SettingsSection";

function SettingsDangerZone({
  children,
  className,
}: { children: React.ReactNode; className?: string }) {
  return (
    <SettingsZone variant="danger" title="Danger Zone" className={className}>
      {children}
    </SettingsZone>
  );
}

SettingsDangerZone.displayName = "SettingsDangerZone";

type SettingsZoneAction = {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
};

const zoneButtonProps: Record<
  SettingsZoneVariant,
  { variant: "destructive" | "primary"; color?: "danger" | "warning" }
> = {
  danger: { variant: "destructive" },
  warning: { variant: "primary", color: "warning" },
};

function SettingsZoneRow({
  title,
  description,
  action,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
  action: SettingsZoneAction;
}) {
  const zoneVariant = React.useContext(SettingsZoneContext);
  const btnProps = zoneButtonProps[zoneVariant];

  return (
    <div className="flex items-center justify-between p-4">
      <div className="space-y-1">
        <p className="font-medium text-gray-12 text-sm">{title}</p>
        <p className="text-gray-11 text-sm">{description}</p>
      </div>
      <Button
        variant={btnProps.variant}
        color={btnProps.color}
        size="md"
        className={cn("shrink-0 px-3", action.className)}
        loading={action.loading}
        disabled={action.disabled}
        onClick={action.onClick}
      >
        {action.label}
      </Button>
    </div>
  );
}

SettingsZoneRow.displayName = "SettingsZoneRow";

function SettingsShell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <main
      className={cn(
        "w-225 flex flex-col justify-center items-center gap-6 mx-auto my-14",
        className,
      )}
    >
      {children}
    </main>
  );
}

SettingsShell.displayName = "SettingsShell";

export {
  SettingCard,
  SettingCardGroup,
  SettingsDangerZone,
  SettingsSection,
  SettingsShell,
  SettingsZone,
  SettingsZoneRow,
};
